import React, { useState, useEffect } from "react";
import { useIntl } from "react-intl";
import { useHistory, useParams } from "react-router-dom";
import { object, string, date, number, array } from "yup";
import { Card, Form, Row, Spinner } from "@masterteam/mt-design-react";

import Tabs from "../Tabs";
import Risks from "./Risks";
import Projects from "./Projects";
import Stakeholders from "./Stakeholders";
import BasicInformation from "./BasicInformation";
import ConfirmationModal from "../ConfirmationModal";
import PerformanceIndicators from "./PerformanceIndicators";
import History from "./History";
import Header from "./Header";
import Steps from "../Steps";

import api from "../../providers/api";
import { addToast } from "../../helpers";

import "./style.scss";
import { REQUEST_STATUSES } from "../../configs/enums";

const validationSchema = object().shape({
  Code: string(),
  ArabicName: string().required(" "),
  EnglishName: string().required(" "),
  ArabicDescription: string().required(" "),
  EnglishDescription: string().required(" "),
  OwnerModel: object().required(" ").nullable(),
  Department: object().required(" ").nullable(),
  Initiatives: array().of(object()),
  Duration: number().min(0, " "),
  KeyResults: string().required(" "),
  StartDate: date().required(" "),
  Strategy: object().required(" ").nullable(),
  StrategicObjective: object().required(" ").nullable(),
  Attribute: object().nullable(),
  KPIs: array().of(object()),
  InitiativeSpecificKPIs: array().of(object()),
  Stakeholders: array().of(object()),
  Projects: array().of(object()),
  Risks: array().of(object())
});

const AddNewInitiative = () => {
  const intl = useIntl();
  const { id } = useParams();
  const history = useHistory();
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [requestIsLoading, setRequestIsLoading] = useState(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [request, setRequest] = useState({});
  const [isDraft, setIsDraft] = useState(false);

  const [lists, setLists] = useState({
    departments: [],
    users: [],
    epmoUsers: [],
    strategicObjectives: [],
    kpis: [],
    risks: []
  });

  const [initialValues, setInitialValues] = useState({
    Code: "",
    ArabicName: "",
    EnglishName: "",
    ArabicDescription: "",
    EnglishDescription: "",
    OwnerModel: null,
    Department: null,
    Initiatives: [],
    Duration: 0,
    KeyResults: "",
    StartDate: new Date(),
    Strategy: null,
    StrategicObjective: null,
    Attribute: null,
    KPIs: [],
    InitiativeSpecificKPIs: [],
    Stakeholders: [],
    Projects: [],
    Risks: []
  });

  const tabs = [
    {
      ID: 0,
      name: intl.formatMessage({ id: "initiative.basic-information" }),
      component: <BasicInformation lists={lists} isReadOnly={isReadOnly} />
    },
    {
      ID: 1,
      name: intl.formatMessage({
        id: "initiative.initiative-performance-indicators"
      }),
      component: <PerformanceIndicators isReadOnly={isReadOnly} />
    },
    {
      ID: 2,
      name: intl.formatMessage({ id: "initiative.stakeholders" }),
      component: <Stakeholders lists={lists} isReadOnly={isReadOnly} />
    },
    {
      ID: 3,
      name: intl.formatMessage({ id: "initiative.projects" }),
      component: <Projects lists={lists} isReadOnly={isReadOnly} />
    },
    {
      ID: 4,
      name: intl.formatMessage({ id: "initiative.risks" }),
      component: <Risks lists={lists} isReadOnly={isReadOnly} />
    },
    {
      ID: 5,
      name: intl.formatMessage({ id: "initiative.history" }),
      component: <History data={request?.WFHistory} />
    }
  ];

  const getLists = async () => {
    setIsLoading(true);
    try {
      const [
        usersRes,
        epmoUsersRes,
        departmentsRes,
        initiativesRes,
        portfoliosRes,
        programsRes,
        projectsRes,
        strategiesRes
      ] = await Promise.all([
        api.user.getAll(),
        api.user.getPPlusEPMO(),
        api.orgStructure.getAll(),
        api.pPlus.getInitiatives(),
        api.pPlus.getPortfolios(),
        api.pPlus.getPrograms(),
        api.pPlus.getProjects(),
        api.strategy.getAllWithKPIs()
      ]);
      setLists((currentLists) => ({
        ...currentLists,
        users: usersRes.StatusCode === "Success" ? usersRes.Data : [],
        epmoUsers:
          epmoUsersRes.StatusCode === "Success" ? epmoUsersRes.Data : [],
        departments:
          departmentsRes.StatusCode === "Success" ? departmentsRes.Data : [],
        initiatives:
          initiativesRes.StatusCode === "Success" ? initiativesRes.Data : [],
        portfolios:
          portfoliosRes.StatusCode === "Success" ? portfoliosRes.Data : [],
        programs: programsRes.StatusCode === "Success" ? programsRes.Data : [],
        projects: projectsRes.StatusCode === "Success" ? projectsRes.Data : [],
        strategies:
          strategiesRes.StatusCode === "Success" ? strategiesRes.Data : []
      }));

      if (id) {
        await getRequestData(strategiesRes.Data);
      }
    } catch (error) {
      addToast.error(error?.message, {
        toastId: "get-users-error"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getRequestData = async (strategyLists) => {
    setRequestIsLoading(true);
    try {
      const res = await api.processes.getRequestById(id);
      setIsReadOnly(res.Data?.Status !== 0 && res.Data?.Status !== 6);

      const selectedStrategy = strategyLists?.find(
        (e) =>
          e.ID === res?.Data?.Form?.StrategicObjective?.Perspective?.Strategy.ID
      );
      const selectedSO = selectedStrategy?.StrategicObjectives?.find(
        (e) => e.ID === res?.Data?.Form?.StrategicObjective?.ID
      );

      const selectedSubSO = selectedSO?.StrategicSubObjective?.find(
        (e) => e.ID === res?.Data?.Form?.Attribute?.ID
      );

      setInitialValues({
        ...res?.Data?.Form,
        Strategy: selectedStrategy,
        StrategicObjective: selectedSO,
        Attribute: selectedSubSO || null,
        RequestID: res?.Data?.ID,
        StartDate: new Date(res?.Data?.Form?.StartDate)
      });
      setRequest(res?.Data);
    } catch (err) {
      addToast.error(err.message, {
        toastId: "errorFetchingData"
      });
    } finally {
      setRequestIsLoading(false);
    }
  };

  const refetchData = async () => {
    try {
      const res = await api.strategy.getAllWithKPIs();
      await getRequestData(res?.Data);
    } catch (err) {
      addToast.error(err.message, {
        toastId: "errorFetchingData"
      });
    }
  };

  const onSubmit = async (values) => {
    setIsSubmitting(true);
    try {
      const body = {
        ...values,
        IsDraft: isDraft,
        RequestID: id || 0
      };

      const res = await api.processes.createInitiative(body);

      const { StatusCode } = res;

      if (StatusCode === "Success") {
        addToast.success("toast.submit");
        history.push("/");
      }
    } catch (error) {
      addToast.error(error?.message, {
        toastId: "get-users-error"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    getLists();
  }, []);

  if (isLoading || requestIsLoading)
    return (
      <Row justify="center">
        <Spinner size="large" />
      </Row>
    );

  const currentStep = request?.Steps?.find(
    (e) => e?.Status === REQUEST_STATUSES.PENDING
  );

  return (
    <Card className="add-new-initiative">
      <Form
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={() => {
          setIsConfirmationModalOpen(true);
        }}
      >
        {({ values }) => (
          <>
            <Header
              canApprove={currentStep?.CanApprove}
              getData={refetchData}
              data={initialValues}
              isReadOnly={isReadOnly}
              onSubmit={(values, isDraft) => {
                setIsConfirmationModalOpen(true);
                setIsDraft(isDraft);
              }}
            />
            <Steps steps={request?.Steps} />
            <Tabs tabs={tabs} lists={lists} />

            {isConfirmationModalOpen && (
              <ConfirmationModal
                isOpen={isConfirmationModalOpen}
                title={intl.formatMessage({ id: "add-initiative" })}
                closeModal={() => {
                  setIsConfirmationModalOpen(false);
                  addToast.error("toast.cancel");
                }}
                handleSubmit={() => {
                  onSubmit(values, false);
                }}
                ButtonStatus={isSubmitting}
              />
            )}
          </>
        )}
      </Form>
    </Card>
  );
};

export default AddNewInitiative;
